import * as React from "react";

import { Button, IconButton, Input } from "Components/ui";
import { Plus, Trash2 } from "Components/ui/icons";
import { ContactDraft, MAX_CONTACTS, newContact } from "../rules";

interface ContactsEditorProps {
  contacts: ContactDraft[];
  errors: Record<string, string>;
  listError: string | null;
  disabled: boolean;
  onChange: (next: ContactDraft[]) => void;
}

/** Billing contacts (name + email and/or WhatsApp number) who receive reminders and receipts. */
const ContactsEditor: React.FC<ContactsEditorProps> = ({
  contacts,
  errors,
  listError,
  disabled,
  onChange,
}) => {
  const update = (key: string, patch: Partial<ContactDraft>) =>
    onChange(contacts.map((contact) => (contact.key === key ? { ...contact, ...patch } : contact)));

  return (
    <div className="space-y-2">
      {contacts.length === 0 && (
        <p className="text-sm text-warning-text">
          No contacts: reminders and receipts can't be sent until you add one.
        </p>
      )}
      {contacts.map((contact, index) => {
        const error = errors[contact.key];
        const errorId = `billing-contact-${contact.key}-error`;
        return (
          <div key={contact.key}>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                type="text"
                value={contact.name}
                disabled={disabled}
                maxLength={120}
                placeholder="Name (optional)"
                aria-label={`Contact ${index + 1} name`}
                onChange={(event) => update(contact.key, { name: event.target.value })}
                wrapperClassName="sm:w-1/4"
              />
              <Input
                type="email"
                value={contact.email}
                disabled={disabled}
                maxLength={254}
                placeholder="accounts@client.com"
                aria-label={`Contact ${index + 1} email`}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                onChange={(event) => update(contact.key, { email: event.target.value })}
                wrapperClassName="flex-1"
              />
              <Input
                type="tel"
                inputMode="tel"
                value={contact.phone}
                disabled={disabled}
                maxLength={20}
                placeholder="WhatsApp (98765 43210)"
                aria-label={`Contact ${index + 1} WhatsApp number`}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                onChange={(event) => update(contact.key, { phone: event.target.value })}
                wrapperClassName="sm:w-1/4"
              />
              {!disabled && (
                <IconButton
                  label={`Remove contact ${index + 1}`}
                  icon={<Trash2 aria-hidden="true" />}
                  onClick={() => onChange(contacts.filter((entry) => entry.key !== contact.key))}
                  className="self-end sm:self-auto"
                />
              )}
            </div>
            {error && (
              <p id={errorId} className="mt-1 text-xs text-danger-text">
                {error}
              </p>
            )}
          </div>
        );
      })}
      {listError && <p className="text-xs text-danger-text">{listError}</p>}
      {!disabled && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onChange([...contacts, newContact()])}
          disabled={contacts.length >= MAX_CONTACTS}
          leftIcon={<Plus aria-hidden="true" />}
        >
          Add contact
        </Button>
      )}
    </div>
  );
};

export default ContactsEditor;
